import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from "recharts";
import { TrendingUp, Loader2, Play, Save } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtCurrency, fmtPercent, formatNumberWithSeparator } from "@/lib/format";
import VendorParametersSelect from "@/components/VendorParametersSelect";
import { useActiveCampaign } from "@/contexts/ActiveCampaignContext";

export default function BudgetSensitivity() {
  const { campaigns, activeCampaignId, activeCampaign } = useActiveCampaign();
  const [curves, setCurves] = useState([]);
  const [curveId, setCurveId] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vpId, setVpId] = useState("");
  const [vpSource, setVpSource] = useState("manual");
  const [useForDashboard, setUseForDashboard] = useState(false);
  const [form, setForm] = useState({
    min_budget: 1000000000,
    max_budget: 5000000000,
    steps: 11,
    tv_split: 0.85,
    tv_cpp: 3537000,
    olv_ecpm: 22000,
    ddf: 1.061,
    in_demo: 0.75,
    viewability: 0.85,
  });
  const [points, setPoints] = useState([]);

  useEffect(() => {
    (async () => {
      const [{ data: cv }, { data: vs }] = await Promise.all([
        api.get("/curve-sets"),
        api.get("/vendors"),
      ]);
      setCurves(cv);
      setVendors(vs);
      if (cv.length) setCurveId(cv[0].id);
    })();
  }, []);

  // When active campaign changes, auto-select the campaign's curve if available
  useEffect(() => {
    if (activeCampaign?.selected_curve_set_id) {
      setCurveId(activeCampaign.selected_curve_set_id);
    }
  }, [activeCampaign?.selected_curve_set_id]);

  const VP_WATCH = ["tv_cpp", "olv_ecpm", "ddf", "in_demo", "viewability"];
  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (vpSource === "vendor" && VP_WATCH.includes(k)) setVpSource("custom");
    // Any input change invalidates a saved-ready state — user must re-run.
    setPoints([]);
  };
  const onPickVendor = (id, vendor) => {
    setVpId(id);
    if (!vendor) { setVpSource("manual"); return; }
    setVpSource("vendor");
    setForm((f) => ({
      ...f,
      tv_cpp: vendor.cpp != null ? vendor.cpp : f.tv_cpp,
      olv_ecpm: vendor.ecpm != null ? vendor.ecpm : f.olv_ecpm,
      in_demo: vendor.in_demo != null ? vendor.in_demo : f.in_demo,
      viewability: vendor.viewability != null ? vendor.viewability : f.viewability,
    }));
    setPoints([]);
  };

  const run = async () => {
    if (!curveId) {
      toast.error("Please select a curve set");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/budget-sensitivity/run", {
        curve_set_id: curveId,
        ...form,
        min_budget: Number(form.min_budget),
        max_budget: Number(form.max_budget),
        steps: Number(form.steps),
        tv_split: Number(form.tv_split),
        tv_cpp: Number(form.tv_cpp),
        olv_ecpm: Number(form.olv_ecpm),
        ddf: Number(form.ddf),
      });
      setPoints(data.points);
      toast.success("Sensitivity analysis completed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (curveId) run();
    // `run` intentionally omitted: we only re-run when the curve changes,
    // not on every form keystroke. `run` closes over the latest form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curveId]);

  const validateForSave = () => {
    if (!activeCampaignId) return "Please select an active campaign first.";
    if (!curveId) return "Please select a curve set.";
    if (!points || points.length === 0) return "Run analysis before saving.";
    const minB = Number(form.min_budget);
    const maxB = Number(form.max_budget);
    if (!(minB >= 0)) return "Min budget must be a valid non-negative number.";
    if (!(maxB > 0)) return "Max budget must be greater than 0.";
    if (maxB <= minB) return "Max budget must be greater than min budget.";
    if (!(Number(form.steps) > 0)) return "Steps must be greater than 0.";
    const tvs = Number(form.tv_split);
    if (!(tvs >= 0 && tvs <= 1)) return "TV split must be between 0 and 1.";
    if (!(Number(form.tv_cpp) > 0)) return "TV CPRP must be greater than 0.";
    if (!(Number(form.olv_ecpm) > 0)) return "OLV eCPM must be greater than 0.";
    if (!(Number(form.ddf) > 0)) return "DDF must be greater than 0.";
    const id = Number(form.in_demo);
    if (!(id >= 0 && id <= 1)) return "In-Demo must be between 0 and 1.";
    const vw = Number(form.viewability);
    if (!(vw >= 0 && vw <= 1)) return "Viewability must be between 0 and 1.";
    return null;
  };

  const onSaveAnalysis = async () => {
    const err = validateForSave();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    const picked = vendors.find((v) => v.id === vpId);
    const inputSnapshot = {
      min_budget: Number(form.min_budget),
      max_budget: Number(form.max_budget),
      steps: Number(form.steps),
      tv_split: Number(form.tv_split),
      tv_cprp: Number(form.tv_cpp),
      olv_ecpm: Number(form.olv_ecpm),
      ddf: Number(form.ddf),
      in_demo: Number(form.in_demo),
      viewability: Number(form.viewability),
      universe_size: activeCampaign?.universe_size ?? null,
      vendor_parameter_id: vpId || null,
      vendor_parameter_name: picked?.vendor_name || null,
      parameter_source_type: vpSource,
    };
    // Build the resultSnapshot in a presentation-ready shape so historical
    // analyses remain stable even if the live engine changes later.
    const resultSnapshot = {
      points,
      budget_vs_reach: points.map((p) => ({
        budget_total: p.budget_total,
        reach_vnp: p.reach_vnp,
        reach_tv_only: p.reach_tv_only,
        reach_tv: p.reach_tv,
        reach_olv: p.reach_olv,
      })),
      effective_cprp: points.map((p) => ({
        budget_total: p.budget_total,
        effective_cprp: p.effective_cprp,
      })),
      detail_table: points.map((p) => ({
        budget_total: p.budget_total,
        budget_tv: p.budget_tv,
        budget_olv: p.budget_olv,
        reach_tv: p.reach_tv,
        reach_olv: p.reach_olv,
        reach_vnp: p.reach_vnp,
        reach_tv_only: p.reach_tv_only,
        effective_cprp: p.effective_cprp,
      })),
    };

    try {
      await api.post("/budget-sensitivity/analyses", {
        campaign_id: activeCampaignId,
        curve_set_id: curveId,
        use_for_dashboard: useForDashboard,
        input_snapshot: inputSnapshot,
        result_snapshot: resultSnapshot,
      });
      toast.success(
        useForDashboard
          ? "Budget sensitivity analysis saved and added to Dashboard."
          : "Budget sensitivity analysis saved successfully.",
      );
    } catch (e) {
      console.error("Save BS analysis failed:", e);
      toast.error(formatApiError(e) || "Failed to save analysis. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const canSave = points.length > 0 && !!activeCampaignId && !!curveId;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
      <div className="xl:col-span-3 card-harmony p-5 space-y-4">
        <h3 className="font-display text-lg font-semibold text-slate-900">Sensitivity Parameters</h3>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Active Campaign</label>
          <div className="mt-2 text-sm font-medium text-slate-900" data-testid="bs-active-campaign">
            {activeCampaignId ? (
              activeCampaign?.campaign_name || <span className="text-slate-400 text-xs">Loading…</span>
            ) : (
              <span className="text-amber-600 text-xs">Pick an active campaign from the Dashboard to save analyses.</span>
            )}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Curve Set</label>
          <Select value={curveId} onValueChange={setCurveId}>
            <SelectTrigger className="mt-2" data-testid="bs-curve-select">
              <SelectValue placeholder="Select curve…" />
            </SelectTrigger>
            <SelectContent>
              {curves.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.curve_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Field label="Min Budget" value={form.min_budget} onChange={(v) => update("min_budget", v)} type="number" format="integer" />
        <Field label="Max Budget" value={form.max_budget} onChange={(v) => update("max_budget", v)} type="number" format="integer" />
        <Field label="Steps" value={form.steps} onChange={(v) => update("steps", v)} type="number" />
        <Field label="TV Split (0-1)" value={form.tv_split} onChange={(v) => update("tv_split", v)} type="number" step="0.05" />
        <Field label="TV CPRP" value={form.tv_cpp} onChange={(v) => update("tv_cpp", v)} type="number" format="integer" />
        <Field label="OLV eCPM" value={form.olv_ecpm} onChange={(v) => update("olv_ecpm", v)} type="number" format="integer" />
        <Field label="DDF" value={form.ddf} onChange={(v) => update("ddf", v)} type="number" step="0.001" />
        <Field label="In-Demo (0-1)" value={form.in_demo} onChange={(v) => update("in_demo", v)} type="number" step="0.01" />
        <Field label="Viewability (0-1)" value={form.viewability} onChange={(v) => update("viewability", v)} type="number" step="0.01" />

        <VendorParametersSelect
          vendors={vendors}
          selectedId={vpId}
          sourceType={vpSource}
          onChange={onPickVendor}
          testId="bs-vp-select"
        />

        <button onClick={run} disabled={busy} className="btn-primary w-full justify-center" data-testid="bs-run">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Run Analysis
        </button>

        <div className="border-t border-slate-100 pt-4 space-y-3">
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#0E73C9] focus:ring-[#0E73C9]"
              checked={useForDashboard}
              onChange={(e) => setUseForDashboard(e.target.checked)}
              disabled={!canSave}
              data-testid="bs-use-for-dashboard"
            />
            <span>
              <span className="font-medium">Use this analysis for Dashboard</span>
              <br />
              <span className="text-slate-500">Pin this result to the active campaign&apos;s Dashboard Budget Sensitivity section.</span>
            </span>
          </label>
          <button
            onClick={onSaveAnalysis}
            disabled={!canSave || saving}
            className="btn-secondary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bs-save-analysis"
            title={!activeCampaignId ? "Select an active campaign first." : (points.length ? "" : "Run analysis first.")}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Analysis
          </button>
          {!activeCampaignId && (
            <p className="text-[11px] text-amber-600" data-testid="bs-no-campaign-hint">
              Saving requires an active campaign — set one on the Dashboard.
            </p>
          )}
        </div>
      </div>

      <div className="xl:col-span-9 space-y-5">
        <div className="card-harmony p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Budget vs Reach</h3>
          <div className="h-80">
            <ResponsiveContainer>
              <AreaChart data={points} margin={{ top: 10, right: 30, left: 0 }}>
                <defs>
                  <linearGradient id="bs-combined" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1CC7B3" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#1CC7B3" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="budget_total" tickFormatter={(v) => `${(v / 1e9).toFixed(1)}B`} fontSize={11} stroke="#64748B" />
                <YAxis tickFormatter={(v) => `${v}%`} fontSize={11} stroke="#64748B" />
                <Tooltip formatter={(v) => `${v?.toFixed(2)}%`} labelFormatter={(v) => `IDR ${fmtCurrency(v)}`} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="reach_vnp" name="Combined Reach" stroke="#1CC7B3" fill="url(#bs-combined)" strokeWidth={2.5} />
                <Line type="monotone" dataKey="reach_tv" name="TV" stroke="#0A3D91" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="reach_olv" name="OLV" stroke="#18B7D8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="reach_tv_only" name="TV Only" stroke="#94A3B8" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-harmony p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Effective CPRP Curve</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="budget_total" tickFormatter={(v) => `${(v / 1e9).toFixed(1)}B`} fontSize={11} stroke="#64748B" />
                <YAxis tickFormatter={(v) => fmtCurrency(v)} fontSize={11} stroke="#64748B" />
                <Tooltip formatter={(v) => `IDR ${fmtCurrency(v)}`} labelFormatter={(v) => `IDR ${fmtCurrency(v)}`} />
                <Line type="monotone" dataKey="effective_cprp" stroke="#0A3D91" strokeWidth={2.5} dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-harmony overflow-hidden">
          <div className="px-5 pt-5">
            <h3 className="font-display text-lg font-semibold text-slate-900">Detail Table</h3>
          </div>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left py-2 px-3">Total Budget</th>
                  <th className="text-right py-2 px-3">TV Budget</th>
                  <th className="text-right py-2 px-3">OLV Budget</th>
                  <th className="text-right py-2 px-3">TV Reach</th>
                  <th className="text-right py-2 px-3">OLV Reach</th>
                  <th className="text-right py-2 px-3">Combined</th>
                  <th className="text-right py-2 px-3">TV Only</th>
                  <th className="text-right py-2 px-3">Eff. CPRP</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <tr key={p.budget_total} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{fmtCurrency(p.budget_total)}</td>
                    <td className="py-2 px-3 text-right">{fmtCurrency(p.budget_tv)}</td>
                    <td className="py-2 px-3 text-right">{fmtCurrency(p.budget_olv)}</td>
                    <td className="py-2 px-3 text-right">{fmtPercent(p.reach_tv)}</td>
                    <td className="py-2 px-3 text-right">{fmtPercent(p.reach_olv)}</td>
                    <td className="py-2 px-3 text-right font-semibold text-[#0B8F80]">{fmtPercent(p.reach_vnp)}</td>
                    <td className="py-2 px-3 text-right text-slate-500">{fmtPercent(p.reach_tv_only)}</td>
                    <td className="py-2 px-3 text-right">{fmtCurrency(p.effective_cprp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, format }) {
  const isInteger = format === "integer";
  const handleChange = (e) => {
    const v = e.target.value;
    if (isInteger) {
      onChange(v.replace(/[^\d]/g, ""));
    } else {
      onChange(v);
    }
  };
  const displayValue = isInteger ? formatNumberWithSeparator(value) : value;
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={isInteger ? "text" : type}
        inputMode={isInteger ? "numeric" : undefined}
        autoComplete="off"
        step={!isInteger ? step : undefined}
        value={displayValue ?? ""}
        onChange={handleChange}
        className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
      />
    </div>
  );
}
